/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { Code, Typography } from '@neo4j-ndl/react';
import { CheckIconOutline } from '@neo4j-ndl/react/icons';
import classNames from 'classnames';

import { type CommonProps } from '../../_common/types';

type ToolCallProps = {
  /** The content of the suggestion */
  children?: React.ReactNode;
  /** Whether the AI is done with using the tool*/
  isDone?: boolean;
};

/**
 * The component is used to display that an LLM is using a tool.
 * Per default, the component will display the text "Using".
 * If the `isDone` prop is true, the component will display the check icon and the text "Ran".
 *
 * @alpha - Changes to this component may be breaking.
 */
const ToolCall = ({
  children,
  isDone = false,
  ref,
  className,
  style,
  htmlAttributes,
  ...restProps
}: CommonProps<'span', ToolCallProps>) => {
  const classes = classNames(
    'ndl-ai-tool n-inline-flex n-items-center n-gap-token-12 n-flex-row',
    className,
  );

  return (
    <Typography
      variant="body-medium"
      ref={ref}
      className={classes}
      style={style}
      htmlAttributes={htmlAttributes}
      {...restProps}
    >
      <span className="n-text-neutral-text-weak n-capitalize n-inline-flex n-items-center n-gap-token-6 n-flex-row">
        {isDone ? (
          <>
            <CheckIconOutline className="n-size-token-16 n-text-neutral-text-weaker" />
            Ran
          </>
        ) : (
          'Using'
        )}
      </span>
      <Code isRunnable={false}>{children}</Code>
    </Typography>
  );
};

export { ToolCall };
